import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ArrowLeft, Plus, Save, Trash2, Lock, AlertTriangle, Shuffle, Copy, Store, Layers, Pencil, X, Moon, MousePointerClick } from 'lucide-react';
import { btn, btnVoltar, sel, aviso, IconeAcao, Girando, COR_MODULO } from './escala-ui';
import {
    HORAS, PERIODOS, curvaDoGrupo, curvaDoPeriodo, horasDoPeriodo,
    horasDoTurno, horasPagasDoTurno, rotuloTurno, distribuirBreaks, ajustarBreaks, semBreak,
    ordenarGrupos, paletaDosGrupos, chaveCor, corDaCelula, textoDaCelula, corDoTotal, textoDoTotal,
    resumoEfetivo, ehNoturno, gruposIrregulares,
    proporcaoDesenhada, proporcaoRealFiltrada, corSemaforo,
} from './escala-grade-utils';

// O EDITOR — a grade de 24 horas.
//
// O gesto (decisão do Rodrigo, 14/09): UMA linha por cargo+turno, e o break se
// distribui CLICANDO na célula. ⚠️ Clique ESQUERDO aumenta o número (devolve
// alguém do almoço ao chão) e clique DIREITO diminui (manda para o almoço) —
// invertido em 15/09, a pedido: o botão principal do mouse faz a ação "para
// cima". O que se lê na célula é sempre GENTE NO CHÃO, nunca gente em break.
//
// ⚠️ A curva é recalculada no front a cada clique (escala-grade-utils.js, que é
//    ESPELHO testado do backend). A AUTORIDADE é o servidor: ao salvar, o PUT
//    recalcula e devolve o resultado dele.

const API = '/api/rh/dimensionamento-escala';
const LOJAS = ['ANV', 'BVV', 'FER', 'GTV', 'MAL', 'MOX', 'PCI', 'PCS', 'RDP', 'SED', 'SVV', 'VEL', 'VSH'];
const token = () => localStorage.getItem('authToken');
const auth = () => ({ Authorization: `Bearer ${token()}` });

// ⚠️ Medidas fixas da célula: o Rodrigo pediu linhas de MESMA espessura e
//    quadradinhos RETANGULARES com borda. Altura fixa (não `auto`) é o que
//    impede uma linha com rótulo maior de engordar e sair do registro da linha
//    TOTAL — foi exatamente o que aconteceu na primeira versão.
// ⚠️ Tamanho da célula da grade. Até 09/10 a largura era DERIVADA da altura pela
//    proporção da célula do Excel (90/41 ≈ 2,2 — bem "deitada"), o que ACOPLAVA as
//    duas: para caber as 24 horas sem rolagem horizontal, a largura caía tanto que
//    a altura ia junto e a linha ficava fina demais (42×19). Como o número da
//    célula tem só 1–2 dígitos, DESACOPLAMOS — a largura é escolhida só para caber
//    na tela (mata a barra de rolagem) e a altura fica no que lê bem. Medido no DOM
//    de produção em 09/10: 43×27 encaixa as 24h + os totalizadores sem rolagem numa
//    área útil de ~1454px. ⚠️ Largura fixa: numa janela bem mais estreita a barra
//    pode voltar (24 colunas são muitas). A proporção do Excel segue valendo só no
//    PDF/Excel, que têm cálculo próprio.
const CEL_A = 27;    // altura de TODAS as linhas da grade
const CEL_L = 43;    // largura das colunas de HORA — escolhida para caber sem rolagem
const BORDA = '1px solid #111827';

// % com 1 casa e vírgula (pt-BR); sem base vira "—".
const fmtPct = (p) => (p === null || p === undefined) ? '—' : `${Number(p).toFixed(1).replace('.', ',')}%`;

// A "tecla" da dica do gesto — o visual de botão de teclado, que é como todo
// mundo já leu instrução de atalho em algum lugar.
const tecla = {
    background: '#fff', border: '1px solid #bfdbfe', borderBottomWidth: 2,
    borderRadius: 5, padding: '2px 7px', fontWeight: 800, fontSize: 11.5,
    fontFamily: 'inherit', color: '#1e40af', whiteSpace: 'nowrap',
};
// ⚠️ O respiro entre a grade e os números é pedido explícito do Rodrigo
//    (14/09): "a coluna totalizadora não precisa ficar COLADA com a escala,
//    muito menos as de QTD e horas totais — ficou muito parecido com o Excel;
//    no site nós temos espaço e liberdade". A coluna vazia é o que separa os
//    dois blocos sem quebrar o alinhamento das linhas (duas tabelas lado a
//    lado perderiam o registro na primeira rolagem).
const RESPIRO = 8;

const RHDimensionamentoEscalaEditor = ({ id, onSair, onTrocarVersao, modoInicial = 'visualizacao' }) => {
    const [dados, setDados] = useState(null);
    const [grupos, setGrupos] = useState({ 'SEG-QUI': [], 'SEX-DOM': [] });
    const [periodo, setPeriodo] = useState('SEG-QUI');
    const [erro, setErro] = useState('');
    const [ok, setOk] = useState('');
    const [salvando, setSalvando] = useState(false);
    // ⚠️ Trocar a loja é um PUT que regrava a escala inteira — leva o mesmo
    //    tempo de um Salvar, e não sinalizava nada. Sem sinal, o seletor de loja
    //    parece não ter obedecido e a pessoa escolhe outra por cima.
    const [movendo, setMovendo] = useState('');
    const [novo, setNovo] = useState({ cargo: '', id_turno: '', qtd: 1 });
    // ⚠️ A MIRA. Rodrigo (14/09): "fica difícil de se orientar horizontalmente,
    //    principalmente quando os horários preenchidos estão muito longe do nome
    //    do cargo lá na primeira coluna". Numa grade de 24 colunas o olho perde
    //    a linha no meio do caminho — a mira acende a linha E a coluna sob o
    //    cursor, que é o conserto clássico para tabela larga.
    const [mira, setMira] = useState({ linha: null, coluna: null });
    // ⚠️ O índice de todas as versões da rede, para trocar de loja SEM voltar à
    //    lista (Rodrigo, 15/09). Vem de /navegacao, que é de propósito magro.
    const [navegacao, setNavegacao] = useState([]);
    // ⚠️ DOIS conceitos diferentes, e confundi-los foi o defeito da versão
    //    anterior: `podeEditar` é da VERSÃO (o backend decide; arquivada não
    //    pode) e `editando` é da SESSÃO (o que eu estou fazendo agora).
    //    Abrir já no modo de edição fazia o olhinho — que é para OLHAR —
    //    entregar uma tela onde um clique torto muda a escala.
    const [editando, setEditando] = useState(modoInicial === 'edicao');

    const carregar = useCallback(async () => {
        setErro('');
        try {
            const [rv, rc, rn] = await Promise.all([
                fetch(`${API}/versoes/${id}`, { headers: auth() }),
                fetch(`${API}/cargos`, { headers: auth() }),
                fetch(`${API}/navegacao`, { headers: auth() }),
            ]);
            if (rn.ok) setNavegacao(await rn.json());
            if (!rv.ok) throw new Error(`HTTP ${rv.status}`);
            const d = await rv.json();
            const c = rc.ok ? await rc.json() : { cargosDaGrade: [], mapa: [] };
            // ⚠️ A ORDEM vem da dtbRhEscalaCargo — é dado, e governa tanto a
            //    ordenação das linhas quanto a paleta. Muda pela tela de
            //    cadastros, sem deploy.
            const ordemPorCargo = {};
            for (const r of c.mapa || []) {
                if (ordemPorCargo[r.CARGO_ESCALA] === undefined) ordemPorCargo[r.CARGO_ESCALA] = r.ORDEM;
            }
            setDados({ ...d, cargosDaGrade: c.cargosDaGrade || [], ordemPorCargo });
            setGrupos({
                'SEG-QUI': (d.periodos['SEG-QUI'] || []).map((g) => ({ ...g, breaks: { ...g.breaks } })),
                'SEX-DOM': (d.periodos['SEX-DOM'] || []).map((g) => ({ ...g, breaks: { ...g.breaks } })),
            });
        } catch (e) {
            setErro('Erro ao carregar a versão.');
        }
    }, [id]);

    useEffect(() => { carregar(); }, [carregar]);
    // Trocar de versão pela navegação recomeça em visualização: o modo de
    // edição vale para a versão em que ele foi ligado, não para a próxima.
    useEffect(() => { setEditando(modoInicial === 'edicao'); }, [id, modoInicial]);

    const porIdTurno = useMemo(
        () => new Map((dados?.turnos || []).map((t) => [Number(t.ID), t])),
        [dados]
    );

    const brutos = useMemo(() => grupos[periodo] || [], [grupos, periodo]);
    // Ordenado para EXIBIR; o estado continua na ordem de inserção, e o índice
    // real é carregado junto para o clique saber que linha mexer.
    const lista = useMemo(
        () => ordenarGrupos(brutos.map((g, i) => ({ ...g, _i: i })), dados?.ordemPorCargo, porIdTurno),
        [brutos, dados, porIdTurno]
    );
    const paleta = useMemo(() => paletaDosGrupos(lista, porIdTurno), [lista, porIdTurno]);
    const curva = useMemo(() => curvaDoPeriodo(brutos, porIdTurno), [brutos, porIdTurno]);
    const maxCurva = Math.max(...curva, 1);
    const resumo = useMemo(
        () => resumoEfetivo(grupos, porIdTurno, dados?.ordemPorCargo),
        [grupos, porIdTurno, dados]
    );
    // A proporção 12x36 × 5x2 (semáforo). Ambas recalculam a cada clique: a
    // DESENHADA direto dos grupos; a REAL filtrando a matriz do crew (que vem do
    // servidor) pelos cargos que ESTÃO no desenho agora — assim adicionar um
    // cargo ao desenho traz os reais daquele cargo para a conta, ao vivo.
    const propDesenhada = useMemo(
        () => proporcaoDesenhada(grupos, porIdTurno),
        [grupos, porIdTurno]
    );
    // Os cargos desenhados saem do SEX-DOM (a mesma base da desenhada).
    const cargosDesenhados = useMemo(
        () => new Set((grupos['SEX-DOM'] || []).map((g) => g.cargo)),
        [grupos]
    );
    const propReal = useMemo(
        () => proporcaoRealFiltrada(dados?.realPorCargo || {}, cargosDesenhados),
        [dados, cargosDesenhados]
    );
    const versaoEditavel = !!dados?.podeEditar;
    const podeEditar = versaoEditavel && editando;   // o que a GRADE obedece
    const pendentes = brutos.reduce((a, g) => a + Math.max(0, semBreak(g)), 0);
    // O espelho do `pendentes`: quantos breaks foram marcados A MAIS. Os dois
    // existem separados porque a mesma escala pode ter as duas coisas ao mesmo
    // tempo (uma linha faltando, outra sobrando).
    const sobrando = brutos.reduce((a, g) => a + Math.max(0, -semBreak(g)), 0);
    // ⚠️ Nomear QUEM está irregular, não só quantos: "3 pessoas sem break" numa
    //    grade de 12 linhas manda a pessoa caçar. (Rodrigo, 15/09.)
    const irregulares = useMemo(() => gruposIrregulares(brutos), [brutos]);

    // ---- mutações de estado -------------------------------------------------
    const mexer = (indice, fn) => {
        if (!podeEditar) return;
        setGrupos((g) => ({
            ...g,
            [periodo]: g[periodo].map((x, i) => (i === indice ? fn({ ...x, breaks: { ...x.breaks } }) : x)),
        }));
        // A ação deu certo — a mensagem da trava (se havia) deixou de valer.
        setOk(''); setErro('');
    };

    const clicarCelula = (indice, hora, devolver) => {
        // ⚠️⚠️ O CLIQUE É LIVRE — dá para pôr mais breaks do que gente, e é de
        //    propósito (Rodrigo, 15/09: "eu QUERO que permita fazer isso, mas
        //    continue avisando").
        //
        //    A versão anterior BLOQUEAVA o clique quando a conta já fechava, e
        //    ficava pior de usar: para MOVER um break era preciso primeiro
        //    devolver o antigo, adivinhando onde ele estava. Agora se posiciona
        //    o novo e se tira o velho, na ordem que a pessoa quiser.
        //
        //    ⚠️ O que impede a escala errada de chegar ao banco NÃO é o clique:
        //    é o aviso vermelho no rodapé (que nomeia a linha), a linha pintada
        //    na grade e — a trava de verdade — o `salvar`, que recusa enquanto
        //    houver linha irregular, para mais OU para menos.
        mexer(indice, (g) => {
            const turno = porIdTurno.get(Number(g.id_turno));
            if (!turno || !horasDoTurno(turno).includes(hora)) return g;
            const atual = Number(g.breaks[hora] || 0);
            if (devolver) {
                if (atual <= 0) return g;
                g.breaks[hora] = atual - 1;
                if (g.breaks[hora] === 0) delete g.breaks[hora];
            } else {
                // ⚠️ A TRAVA DO ZERO (Rodrigo, 15/09): não dá para mandar ao break
                //    mais gente do que existe NAQUELA HORA — a célula iria a -1, e
                //    "menos uma pessoa no chão" não quer dizer nada. Máximo da
                //    célula é `qtd` (todo mundo no chão), mínimo é 0.
                //    ⚠️ Repare que esta trava é POR HORA e é a única que sobrou.
                //    A do TOTAL (soma dos breaks = qtd) foi de propósito EMBORA:
                //    ela impedia mover um break sem devolver o antigo primeiro.
                //    O total errado é permitido, aparece no aviso e barra o Salvar.
                if (atual >= g.qtd) return g;
                g.breaks[hora] = atual + 1;
            }
            return g;
        });
    };

    const mudarQtd = (indice, qtd) => {
        const n = Math.max(0, Number(qtd) || 0);
        mexer(indice, (g) => {
            const turno = porIdTurno.get(Number(g.id_turno));
            return { ...g, qtd: n, breaks: turno ? ajustarBreaks(g.breaks, g.qtd, n, turno) : {} };
        });
    };

    const redistribuir = (indice) => {
        mexer(indice, (g) => {
            const turno = porIdTurno.get(Number(g.id_turno));
            return { ...g, breaks: turno ? distribuirBreaks(g.qtd, turno) : {} };
        });
    };

    const remover = (indice) => {
        if (!podeEditar) return;
        setGrupos((g) => ({ ...g, [periodo]: g[periodo].filter((_, i) => i !== indice) }));
        setOk('');
    };

    const adicionar = () => {
        if (!podeEditar || !novo.cargo || !novo.id_turno) return;
        const turno = porIdTurno.get(Number(novo.id_turno));
        if (!turno) return;
        if (brutos.some((g) => g.cargo === novo.cargo && Number(g.id_turno) === Number(novo.id_turno))) {
            setErro('Esse cargo já tem uma linha nesse turno — ajuste a quantidade dela em vez de criar outra.');
            return;
        }
        const qtd = Math.max(1, Number(novo.qtd) || 1);
        setGrupos((g) => ({
            ...g,
            [periodo]: [...g[periodo], {
                cargo: novo.cargo, id_turno: Number(novo.id_turno),
                turno: rotuloTurno(turno), escala: turno.ESCALA,
                qtd, breaks: distribuirBreaks(qtd, turno),
            }],
        }));
        setErro(''); setOk('');
    };

    const copiarParaOutroPeriodo = () => {
        if (!podeEditar) return;
        const outro = periodo === 'SEG-QUI' ? 'SEX-DOM' : 'SEG-QUI';
        if (!window.confirm(`Copiar tudo de ${periodo} para ${outro}? O conteúdo atual de ${outro} será substituído.`)) return;
        setGrupos((g) => ({ ...g, [outro]: g[periodo].map((x) => ({ ...x, breaks: { ...x.breaks } })) }));
        setOk(`Conteúdo copiado para ${outro}. Ajuste o 5x2 lá — é nele que as folgas do pior cenário são rodadas.`);
    };

    const salvar = async () => {
        // ⚠️ Olha IRREGULARES, não `pendentes > 0`. `pendentes` soma só o que
        //    falta (`Math.max(0, ...)`), então uma linha com break A MAIS que
        //    pessoas passava batido — número negativo não é maior que zero.
        //    A trava do clique impede chegar nesse estado hoje, mas a rede de
        //    segurança do salvamento não pode depender dela.
        if (irregulares.length > 0) {
            const nomes = irregulares.map(({ grupo }) => grupo.cargo).join(', ');
            setErro(
                `A conta do break não fecha em ${irregulares.length} linha(s) — ${nomes}. ` +
                `Cada pessoa tem exatamente um break de 1h; as linhas em vermelho mostram quais.`
            );
            return;
        }
        setSalvando(true); setErro(''); setOk('');
        try {
            const corpo = {
                periodos: Object.fromEntries(PERIODOS.map((p) => [
                    p, (grupos[p] || []).filter((g) => g.qtd > 0).map((g) => ({
                        cargo: g.cargo, id_turno: Number(g.id_turno), breaks: g.breaks,
                    })),
                ])),
            };
            const r = await fetch(`${API}/versoes/${id}`, {
                method: 'PUT', headers: { ...auth(), 'Content-Type': 'application/json' }, body: JSON.stringify(corpo),
            });
            const d = await r.json().catch(() => ({}));
            if (!r.ok) { setErro(d?.message || 'Erro ao salvar.'); return; }
            await carregar();   // a resposta do servidor é a AUTORIDADE
            setOk('Escala salva. Os números abaixo vieram do servidor.');
        } catch (e) {
            setErro('Erro de rede ao salvar.');
        } finally {
            setSalvando(false);
        }
    };

    // ⚠️ Trocar a LOJA renomeia a versão junto (o nome carrega a sigla). Por isso
    //    é uma ação explícita, com confirmação — não um campo que se edita de
    //    passagem. Serve principalmente para a cópia feita a partir de outra
    //    loja: duplica-se o posicionamento e se diz de quem ele passa a ser.
    const trocarLoja = async (novaLoja) => {
        if (!novaLoja || novaLoja === dados.versao.LOJA) return;
        if (!window.confirm(`Mover esta escala para a ${novaLoja}? Ela será renomeada — o nome carrega a sigla da loja.`)) return;
        setMovendo(novaLoja); setErro(''); setOk('');
        try {
        const r = await fetch(`${API}/versoes/${id}`, {
            method: 'PUT', headers: { ...auth(), 'Content-Type': 'application/json' },
            body: JSON.stringify({
                loja: novaLoja,
                periodos: Object.fromEntries(PERIODOS.map((p) => [
                    p, (grupos[p] || []).filter((g) => g.qtd > 0).map((g) => ({
                        cargo: g.cargo, id_turno: Number(g.id_turno), breaks: g.breaks,
                    })),
                ])),
            }),
        });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) { setErro(d?.message || 'Erro ao mover a escala.'); return; }
        await carregar();
        setOk(`Escala movida para a ${novaLoja}.`);
        } catch (e) {
            setErro('A mudança não chegou ao servidor. Confira a conexão e tente de novo.');
        } finally {
            setMovendo('');
        }
    };

    // Sair da edição descartando o que não foi salvo. Recarrega do servidor em
    // vez de tentar desfazer no estado: o servidor é a autoridade, e "voltar ao
    // que estava" é exatamente o que ele devolve.
    const cancelarEdicao = async () => {
        if (!window.confirm('Sair da edição? O que não foi salvo se perde.')) return;
        await carregar();
        setEditando(false);
        setErro(''); setOk('');
    };

    // ⚠️ O EXPORT saiu desta tela (Rodrigo, 15/09) e vai ganhar a sua:
    //    exportar uma escala por vez não é o uso real (ele quer o lote em
    //    Excel), e ainda vem um export GRÁFICO em paisagem, feito para a loja
    //    ver a distribuição no chão. Duas coisas diferentes, nenhuma delas um
    //    botão de canto aqui. A rota /versoes/:id/exportar continua de pé.

    if (!dados) {
        return (
            <div>
                <button onClick={onSair} style={btnVoltar}><ArrowLeft size={16} /> Voltar</button>
                <p style={{ color: '#64748b', marginTop: 16 }}>{erro || 'Carregando…'}</p>
            </div>
        );
    }

    // ⚠️ O confronto com o ORÇADO saiu desta tela (Rodrigo, 15/09: "deixa isso
    //    pra depois quando definirmos algumas coisas") — e faz sentido: a tabela
    //    de orçamento está VAZIA por decisão, então o indicador só mostraria
    //    "—". A rota continua devolvendo `orcamento`; é só a tela que não o usa.

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
                <button onClick={onSair} style={btnVoltar}><ArrowLeft size={16} /> Voltar</button>
                <div style={{ fontWeight: 800, fontSize: 16 }}>{dados.versao.LOJA} · {dados.versao.NOME_VERSAO}</div>
                <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: editando ? '#fee2e2' : '#e2e8f0', color: editando ? '#991b1b' : '#475569',
                    padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700,
                }}>
                    {editando ? <><Pencil size={12} /> Editando</> : <><Lock size={12} /> {dados.versao.STATUS} · somente leitura</>}
                </span>
                <div style={{ flex: 1 }} />
                {/* ⚠️ O Exportar saiu daqui (Rodrigo, 15/09): ele vai ganhar TELA
                    PRÓPRIA, porque exportar uma escala de cada vez não é o uso
                    real — e ainda vem o export gráfico em paisagem para a loja. */}
                {!editando && versaoEditavel && (
                    <button onClick={() => { setEditando(true); setOk(''); setErro(''); }}
                        title="Entrar no modo de edição — o botão Salvar aparece depois disso"
                        style={btn(COR_MODULO, '#fff')}>
                        <Pencil size={14} /> Editar
                    </button>
                )}
                {editando && (
                    <>
                        <button onClick={cancelarEdicao} title="Sair da edição e descartar o que não foi salvo"
                            style={{ ...btn('#fff', '#475569'), border: '1px solid #e2e8f0' }}>
                            <X size={14} /> Cancelar
                        </button>
                        <button onClick={salvar} disabled={salvando || !!movendo} style={btn(COR_MODULO, '#fff')}>
                            {salvando ? <Girando size={14} /> : <Save size={14} />}
                            {salvando ? 'Salvando…' : 'Salvar'}
                        </button>
                    </>
                )}
            </div>

            {/* ⚠️ A condição é `!versaoEditavel`, NÃO `!podeEditar`: desde que o
                modo de visualização existe, `!podeEditar` é verdadeiro sempre que
                não se está editando — e este aviso passaria a chamar de
                "arquivada" toda versão que alguém abrisse só para olhar. */}
            {!versaoEditavel && (
                <div style={aviso('#e2e8f0', '#334155')}>
                    Versão <b>arquivada</b> não pode ser alterada. Para mexer, volte e use
                    <b> Republicar</b> — ou <b>Duplicar</b>, que cria um Rascunho.
                </div>
            )}
            {erro && <div style={aviso('#fee2e2', '#991b1b')}>{erro}</div>}
            {ok && <div style={aviso('#dcfce7', '#166534')}>{ok}</div>}

            {/* ⚠️ NAVEGAÇÃO — trocar de loja e de versão sem voltar à lista.
                Rodrigo, 15/09: "temos que ter botões para migrar pra outras
                lojas e navegar por outras escalas sem ter que voltar pra tabela
                anterior." */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14, padding: '10px 12px', background: '#f8fafc', borderRadius: 8 }}>
                <Store size={15} color="#475569" />
                <select value={dados.versao.LOJA} style={sel}
                    onChange={(e) => {
                        // A loja escolhida aqui NAVEGA; mover a escala é o botão ao lado.
                        const primeira = navegacao.find((v) => v.LOJA === e.target.value);
                        if (primeira) onTrocarVersao?.(primeira.ID);
                        else setErro(`A ${e.target.value} ainda não tem nenhuma versão.`);
                    }}>
                    {LOJAS.map((x) => (
                        <option key={x} value={x}>
                            {x}{navegacao.some((v) => v.LOJA === x) ? '' : ' (sem versão)'}
                        </option>
                    ))}
                </select>

                <Layers size={15} color="#475569" />
                <select value={id} style={{ ...sel, minWidth: 210 }}
                    onChange={(e) => onTrocarVersao?.(Number(e.target.value))}>
                    {navegacao.filter((v) => v.LOJA === dados.versao.LOJA).map((v) => (
                        <option key={v.ID} value={v.ID}>
                            {v.NOME_VERSAO} · {v.STATUS}
                        </option>
                    ))}
                </select>

                {podeEditar && (
                    <>
                        <div style={{ width: 1, height: 22, background: '#cbd5e1' }} />
                        <span style={{ fontSize: 12, color: '#64748b' }}>Mover esta escala para:</span>
                        <select value="" style={sel} disabled={!!movendo || salvando}
                            onChange={(e) => trocarLoja(e.target.value)}>
                            <option value="">escolha a loja…</option>
                            {LOJAS.filter((x) => x !== dados.versao.LOJA).map((x) => <option key={x} value={x}>{x}</option>)}
                        </select>
                        {movendo && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, color: COR_MODULO }}>
                                <Girando size={13} /> movendo para {movendo}…
                            </span>
                        )}
                    </>
                )}
            </div>

            <div style={{ display: 'flex', gap: 6, marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                {PERIODOS.map((p) => (
                    <button key={p} onClick={() => setPeriodo(p)}
                        style={{
                            ...btn(periodo === p ? COR_MODULO : '#fff', periodo === p ? '#fff' : '#475569'),
                            border: periodo === p ? 'none' : '1px solid #e2e8f0', padding: '8px 16px', fontSize: 13,
                        }}>
                        {p}
                    </button>
                ))}
                <span style={{ fontSize: 12, color: '#64748b', marginLeft: 6 }}>
                    {periodo === 'SEG-QUI'
                        ? 'O pior cenário: dias de menor movimento, onde as folgas do 5x2 são rodadas.'
                        : 'A semana cheia: é daqui que sai o efetivo do 5x2.'}
                </span>
                {podeEditar && (
                    <button onClick={copiarParaOutroPeriodo} title={`Substitui o conteúdo de ${periodo === 'SEG-QUI' ? 'SEX-DOM' : 'SEG-QUI'} por este`}
                        style={{ ...btn('#fff', '#475569'), border: '1px solid #e2e8f0', marginLeft: 'auto' }}>
                        <Copy size={14} /> Copiar para {periodo === 'SEG-QUI' ? 'SEX-DOM' : 'SEG-QUI'}
                    </button>
                )}
            </div>

            {podeEditar && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12, padding: 10, background: '#f8fafc', borderRadius: 8 }}>
                    <select value={novo.cargo} onChange={(e) => setNovo({ ...novo, cargo: e.target.value })} style={sel}>
                        <option value="">Cargo…</option>
                        {dados.cargosDaGrade.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <select value={novo.id_turno} onChange={(e) => setNovo({ ...novo, id_turno: e.target.value })} style={sel}>
                        <option value="">Turno…</option>
                        {(dados.turnos || []).filter((t) => t.ATIVO).map((t) => (
                            <option key={t.ID} value={t.ID}>{t.rotulo} ({t.span}h)</option>
                        ))}
                    </select>
                    <input type="number" min={1} value={novo.qtd} onChange={(e) => setNovo({ ...novo, qtd: e.target.value })}
                        style={{ ...sel, width: 70 }} title="Quantas pessoas" />
                    <button onClick={adicionar} style={btn(COR_MODULO, '#fff')}><Plus size={14} /> Adicionar</button>
                    <span style={{ fontSize: 12, color: '#64748b' }}>
                        Clique numa célula para mandar 1 pessoa ao break · botão direito devolve.
                    </span>
                </div>
            )}

            {/* A GRADE */}
            {/* ⚠️ A DICA DO GESTO (Rodrigo, 15/09: "como a ferramenta é nova, essa
                tooltip será muito útil no início"). Fica ACIMA da grade e é
                ESTÁTICA — só depende do modo de edição, nunca do estado da
                escala. Aviso que aparece e some a cada clique empurra a grade
                para baixo, que foi o defeito que tirou o de irregularidade
                daqui. */}
            {podeEditar && (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
                    background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af',
                    borderRadius: 8, padding: '9px 14px', marginBottom: 10, fontSize: 12.5,
                }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 800 }}>
                        <MousePointerClick size={15} /> Como marcar o break
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <kbd style={tecla}>clique esquerdo</kbd>
                        <b>aumenta</b> o número — tira 1 pessoa do almoço e devolve ao chão
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <kbd style={tecla}>clique direito</kbd>
                        <b>diminui</b> — manda 1 pessoa para o almoço naquela hora
                    </span>
                    <span style={{ opacity: 0.85 }}>
                        Ou use o <Shuffle size={12} style={{ verticalAlign: 'middle' }} /> da linha, que reparte tudo de uma vez.
                    </span>
                </div>
            )}

            {/* ⚠️ padding-left: 0 é proposital e faz PAR com a coluna congelada.
                A coluna "Cargo · turno" é sticky em left:0 e pina na borda ESQUERDA
                deste container rolável. Um padding-left aqui deixa uma faixa de 8px
                à esquerda da coluna por onde o conteúdo rolando reaparece — num
                container com scroll o conteúdo passa POR BAIXO do padding. Era o
                "filete" que rolava à esquerda da coluna. Por isso o lado esquerdo
                vai a 0; os demais mantêm o respiro. Medido no DOM de produção em
                2026-10-09 (faixa 8px → 0). */}
            <div style={{ overflowX: 'auto', background: '#fff', borderRadius: 10, padding: '8px 8px 8px 0' }}
                onMouseLeave={() => setMira({ linha: null, coluna: null })}>
                {/* ⚠️ overflow: 'visible' NÃO é cosmético — é o que faz a coluna
                    "Cargo · turno" CONGELAR ao rolar na horizontal. Há uma regra
                    global `table { overflow: hidden }` no index.css (para clipar o
                    cantinho arredondado das tabelas comuns do portal). Qualquer
                    ancestral com `overflow ≠ visible` entre a célula sticky e o div
                    rolável "captura" o `position: sticky` e o anula — a coluna
                    rolava junto e sumia. Medido no DOM de produção em 2026-10-08
                    (offset -564px → +8px ao forçar visible). Esta tabela não tem
                    canto arredondado próprio (o raio é do div de fora), então
                    liberar o overflow aqui não tem custo visual. */}
                <table style={{ borderCollapse: 'separate', borderSpacing: 0, fontSize: 12, tableLayout: 'fixed', overflow: 'visible' }}>
                    <thead>
                        <tr style={{ height: 30 }}>
                            <th style={{ ...thRotulo, position: 'sticky', left: 0, zIndex: 3 }}>Cargo · turno</th>
                            {HORAS.map((h, i) => (
                                <th key={h}
                                    style={{
                                        ...thHora,
                                        // Preto com branco, como o cabeçalho da tabela de versões.
                                        // A coluna sob o cursor clareia — é o que amarra "que hora
                                        // é essa" ao número lá embaixo.
                                        background: mira.coluna === i ? '#475569' : '#0f172a',
                                    }}>
                                    {h}
                                </th>
                            ))}
                            <th style={{ width: RESPIRO, border: 'none', background: '#fff' }} />
                            <th style={thNum}>Qtd</th>
                            <th style={thNum}>Horas</th>
                            {podeEditar && <th style={{ ...thNum, width: 66 }}>Ações</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {lista.length === 0 && (
                            <tr><td colSpan={HORAS.length + 5} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>
                                Nenhuma linha neste período. {podeEditar ? 'Use o formulário acima para começar.' : ''}
                            </td></tr>
                        )}
                        {lista.map((g, iLinha) => {
                            const turno = porIdTurno.get(Number(g.id_turno));
                            const linhaCurva = curvaDoGrupo(g, porIdTurno);
                            const faltam = semBreak(g);
                            const cor = paleta.get(chaveCor(g));
                            const naMira = mira.linha === iLinha;
                            const irregular = faltam !== 0;
                            return (
                                <tr key={chaveCor(g)} style={{ height: CEL_A }}
                                    onMouseEnter={() => setMira((m) => ({ ...m, linha: iLinha }))}>
                                    <td style={{
                                        ...tdRotulo,
                                        position: 'sticky', left: 0, zIndex: 2,
                                        // ⚠️ IRREGULAR vence a mira: a linha que não fecha a conta
                                        //    fica vermelha o tempo todo, e a faixa lateral também —
                                        //    é o sinal de "é ESTA aqui" que o aviso de cima só
                                        //    conseguia dizer por escrito.
                                        // Fora isso, a linha sob o cursor acende na cor do PRÓPRIO
                                        // cargo: o vínculo "esta faixa colorida é este nome" deixa
                                        // de depender da memória do olho ao atravessar 24 colunas.
                                        background: irregular ? '#fee2e2' : naMira ? `hsl(${cor ? cor.hue : 210} 65% 94%)` : '#fff',
                                        boxShadow: `inset 5px 0 0 0 ${irregular ? '#dc2626' : cor ? cor.base : '#cbd5e1'}`,
                                        fontWeight: naMira || irregular ? 800 : 600,
                                    }}
                                        title={`${g.cargo} · ${turno ? rotuloTurno(turno) : ''}`}>
                                        <span style={{ color: '#0f172a' }}>{g.cargo}</span>
                                        <span style={{ color: '#64748b', fontWeight: 500 }}> · {turno ? rotuloTurno(turno) : `turno ${g.id_turno}?`}</span>
                                        {/* ⚠️ Marca o turno noturno, que é o que explica a coluna
                                            Horas render menos na MESMA escala — sem ela o número
                                            parece erro de conta.
                                            ⚠️ Era um emoji 🌙 e virou o ícone do lucide: emoji é
                                            desenhado pela FONTE DO SISTEMA, então muda de forma (e
                                            de cor) em cada máquina — no Windows a lua sai deitada e
                                            amarela, parecendo outra coisa. O ícone é SVG: igual em
                                            todo lugar e na cor que a tela mandar. */}
                                        {turno && ehNoturno(turno) && (
                                            <span title="Turno noturno — jornada reduzida em 1h"
                                                style={{ marginLeft: 5, display: 'inline-flex', verticalAlign: '-2px' }}>
                                                <Moon size={12} strokeWidth={2.5} color="#475569" />
                                            </span>
                                        )}
                                        {faltam > 0 && <b style={{ color: '#b91c1c' }}> · {faltam} sem break</b>}
                                        {faltam < 0 && <b style={{ color: '#b91c1c' }}> · {-faltam} break(s) a mais</b>}
                                    </td>
                                    {HORAS.map((h, idx) => {
                                        const v = linhaCurva[idx];
                                        const dentro = v !== null && v !== undefined;
                                        const emBreak = Number(g.breaks[h] || 0);
                                        const naColuna = mira.coluna === idx;
                                        return (
                                            <td key={h}
                                                onMouseEnter={() => setMira({ linha: iLinha, coluna: idx })}
                                                // ⚠️ ESQUERDO AUMENTA o número, DIREITO DIMINUI (Rodrigo,
                                                //    15/09 — era o contrário). O `devolver` é o parâmetro
                                                //    do break: devolver = tirar do almoço = número SOBE.
                                                onClick={() => dentro && clicarCelula(g._i, h, true)}
                                                onContextMenu={(e) => { e.preventDefault(); if (dentro) clicarCelula(g._i, h, false); }}
                                                title={dentro
                                                    ? `${g.cargo} · ${h}h — ${v} no chão${emBreak ? `, ${emBreak} em break` : ''}`
                                                        + (podeEditar ? ' — esquerdo aumenta, direito manda para o break' : '')
                                                    : ''}
                                                style={{
                                                    ...tdHora,
                                                    background: dentro ? corDaCelula(cor, v, g.qtd)
                                                        : irregular ? '#fef2f2'
                                                        : (naColuna || naMira) ? '#f1f5f9' : 'transparent',
                                                    color: textoDaCelula(cor, v, g.qtd),
                                                    border: dentro ? BORDA : '1px solid #eef2f7',
                                                    // Anel POR DENTRO: `outline` empurraria a grade
                                                    // inteira em 2px a cada passagem do mouse.
                                                    boxShadow: (naMira || naColuna)
                                                        ? `inset 0 0 0 ${naMira && naColuna ? 2 : 1}px #0f172a` : 'none',
                                                    cursor: dentro && podeEditar ? 'pointer' : 'default',
                                                    // ⚠️ Negrito em TODAS (Rodrigo, 14/09). Antes só a célula
                                                    //    com break era 800 — e a diferença de peso lia como se
                                                    //    o break fosse "mais importante", quando ele é só outra
                                                    //    informação. Quem marca o break agora é a COR.
                                                    fontWeight: 800,
                                                    userSelect: 'none',
                                                }}>
                                                {dentro ? v : ''}
                                            </td>
                                        );
                                    })}
                                    {/* o respiro entre a escala e os números */}
                                    <td style={{ width: RESPIRO, border: 'none', background: '#fff' }} />
                                    <td style={{ ...tdNumero, background: naMira ? '#e2e8f0' : '#f8fafc', borderRadius: '6px 0 0 6px' }}>
                                        {podeEditar
                                            ? <input type="number" min={0} value={g.qtd} onChange={(e) => mudarQtd(g._i, e.target.value)}
                                                style={{ width: 44, padding: '3px 2px', border: '1px solid #cbd5e1', borderRadius: 6, textAlign: 'center', fontSize: 12, fontWeight: 800 }} />
                                            : g.qtd}
                                    </td>
                                    <td style={{ ...tdNumero, background: naMira ? '#e2e8f0' : '#f8fafc', fontWeight: 800, borderRadius: podeEditar ? 0 : '0 6px 6px 0' }}>
                                        {turno ? g.qtd * horasPagasDoTurno(turno) : 0}
                                    </td>
                                    {podeEditar && (
                                        <td style={{ ...tdNumero, background: naMira ? '#e2e8f0' : '#f8fafc', whiteSpace: 'nowrap', borderRadius: '0 6px 6px 0' }}>
                                            <IconeAcao onClick={() => redistribuir(g._i)} cor="#0284c7"
                                                titulo="Redistribuir os breaks uniformemente pela janela usual"><Shuffle size={15} /></IconeAcao>
                                            <IconeAcao onClick={() => remover(g._i)} cor="#e11d48"
                                                titulo="Remover esta linha da escala"><Trash2 size={15} /></IconeAcao>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}

                        {/* respiro antes do total — ele é outra coisa, não mais uma linha */}
                        <tr style={{ height: 14 }}><td colSpan={HORAS.length + 5} style={{ border: 'none' }} /></tr>

                        {/* A linha TOTAL usa OUTRA escala de cor (verde→amarelo→vermelho):
                            ela não é um cargo, é MOVIMENTO. */}
                        <tr style={{ height: CEL_A + 4 }}
                            onMouseEnter={() => setMira((m) => ({ ...m, linha: 'total' }))}>
                            <td style={{
                                ...tdRotulo, position: 'sticky', left: 0, zIndex: 2,
                                background: '#0f172a', color: '#fff', fontWeight: 800,
                                boxShadow: 'none', borderRadius: '6px 0 0 6px',
                            }}>
                                TOTAL
                            </td>
                            {curva.map((v, i) => (
                                <td key={i} title={`${HORAS[i]}h — ${v} pessoa(s) no chão`}
                                    onMouseEnter={() => setMira({ linha: 'total', coluna: i })}
                                    style={{
                                        ...tdHora, height: CEL_A + 4, border: BORDA, fontWeight: 800,
                                        background: corDoTotal(v, maxCurva), color: textoDoTotal(v, maxCurva),
                                        boxShadow: mira.coluna === i ? 'inset 0 0 0 2px #0f172a' : 'none',
                                    }}>
                                    {v}
                                </td>
                            ))}
                            <td style={{ width: RESPIRO, border: 'none', background: '#fff' }} />
                            <td style={{ ...tdNumero, background: '#0f172a', color: '#fff', fontWeight: 800, borderRadius: '6px 0 0 6px' }}>
                                {brutos.reduce((a, g) => a + g.qtd, 0)}
                            </td>
                            <td style={{ ...tdNumero, background: '#0f172a', color: '#fff', fontWeight: 800, borderRadius: podeEditar ? 0 : '0 6px 6px 0' }}>
                                {horasDoPeriodo(brutos, porIdTurno)}
                            </td>
                            {podeEditar && <td style={{ ...tdNumero, background: '#0f172a', borderRadius: '0 6px 6px 0' }} />}
                        </tr>
                    </tbody>
                </table>
            </div>

            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center', marginTop: 8, fontSize: 11, color: '#64748b' }}>
                <span>A célula mostra quem está <b>no chão</b> — a queda (cor mais clara) é o break.</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    Movimento no TOTAL:
                    <i style={{ width: 16, height: 12, background: corDoTotal(1, 10), border: BORDA, display: 'inline-block' }} /> fraco
                    <i style={{ width: 16, height: 12, background: corDoTotal(5, 10), border: BORDA, display: 'inline-block' }} /> médio
                    <i style={{ width: 16, height: 12, background: corDoTotal(10, 10), border: BORDA, display: 'inline-block' }} /> pico
                </span>
                <span>A grade começa às 7h e vira a meia-noite, terminando às 6h.</span>
            </div>

            {/* ⚠️ O aviso de irregularidade também é RODAPÉ (Rodrigo, 15/09): no
                topo, ele aparecia e sumia conforme se clica — e a cada vez EMPURRAVA
                a grade para baixo, justamente enquanto a pessoa mira uma célula. O
                sinal imediato de "é esta linha" já é o vermelho na própria grade;
                aqui fica o detalhe de quem e quanto. */}
            {irregulares.length > 0 && (
                <div style={aviso('#fee2e2', '#991b1b')}>
                    {/* ⚠️ O texto do topo cobre os DOIS erros. Com break SOBRANDO,
                        `pendentes` é 0 — e "0 pessoas sem break" num aviso vermelho
                        faria a pessoa procurar um problema que o texto negou. */}
                    <b>
                        {pendentes > 0 && `${pendentes} pessoa(s) sem horário de break`}
                        {pendentes > 0 && sobrando > 0 && ' · '}
                        {sobrando > 0 && `${sobrando} break(s) a mais do que gente`}
                    </b>
                    {' '}— cada pessoa tem exatamente <b>um</b> break de 1h.
                    <b> Enquanto a conta não fechar, a escala não salva.</b>
                    {/* ⚠️ As linhas ficam marcadas em vermelho na grade; aqui vai
                        o nome de cada uma, para não ser preciso caçar. */}
                    <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
                        {irregulares.map(({ grupo, faltam }, i) => {
                            const turno = porIdTurno.get(Number(grupo.id_turno));
                            return (
                                <li key={i} style={{ marginBottom: 2 }}>
                                    <b>{grupo.cargo} · {turno ? rotuloTurno(turno) : `turno ${grupo.id_turno}`}</b>
                                    {faltam > 0
                                        ? ` — ${faltam} de ${grupo.qtd} sem break`
                                        : ` — ${-faltam} break(s) a mais que pessoas`}
                                </li>
                            );
                        })}
                    </ul>
                    <div style={{ marginTop: 6, fontSize: 12, opacity: 0.9 }}>
                        Clique nas células para distribuir, ou use o
                        <Shuffle size={12} style={{ verticalAlign: 'middle', margin: '0 3px' }} /> da linha, que reparte tudo de uma vez.
                    </div>
                </div>
            )}

            {/* A PROPORÇÃO 12x36 × 5x2 — tabela Desenhada × Real, sobre os cargos
                EFETIVAMENTE DESENHADOS (Rodrigo, 02/10). As duas recalculam a cada
                clique. A Desenhada é efetivo (pessoas, 12x36 em dobro) e a Real é o
                crew ativo da loja nos mesmos cargos — mesma unidade, comparável. A
                linha de % leva o semáforo. */}
            {(() => {
                const cD = corSemaforo(propDesenhada?.faixa);
                const cR = corSemaforo(propReal?.faixa);
                const celProp = (c) => ({ ...tdResumo, background: c.fundo, color: c.texto, fontWeight: 800 });
                return (
                    <div style={{ marginTop: 16 }}>
                        <div style={{ fontSize: 13, fontWeight: 800 }}>
                            Proporção 12x36
                            <span style={{ fontWeight: 400, color: '#64748b', fontSize: 12 }}> — o mix de escalas sobre os cargos desenhados, meta 70%</span>
                        </div>
                        <div style={{ marginTop: 8, border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', display: 'inline-block' }}>
                            <table style={{ borderCollapse: 'collapse', fontSize: 12, background: '#fff' }}>
                                <thead>
                                    <tr style={{ background: '#0f172a' }}>
                                        <th style={{ ...thResumo, textAlign: 'left' }}>Família</th>
                                        <th style={thResumo} title="O efetivo do desenho, com o 12x36 contado em dobro">Desenhada (efetivo)</th>
                                        <th style={thResumo} title="Crew ativo da loja nos cargos desenhados">Real (pessoas)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td style={{ ...tdResumo, textAlign: 'left', fontWeight: 700 }}>12x36</td>
                                        <td style={tdResumo}>{propDesenhada.n12}</td>
                                        <td style={tdResumo}>{propReal.n12}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ ...tdResumo, textAlign: 'left', fontWeight: 700 }}>5x2</td>
                                        <td style={tdResumo}>{propDesenhada.n5}</td>
                                        <td style={tdResumo}>{propReal.n5}</td>
                                    </tr>
                                    {propReal.outros > 0 && (
                                        <tr>
                                            <td style={{ ...tdResumo, textAlign: 'left', fontWeight: 700, color: '#64748b' }} title="4x44, partime, aprendiz — contam no total, não no 12x36">demais famílias</td>
                                            <td style={{ ...tdResumo, color: '#94a3b8' }}>—</td>
                                            <td style={tdResumo}>{propReal.outros}</td>
                                        </tr>
                                    )}
                                    <tr style={{ background: '#0f172a' }}>
                                        <td style={{ ...tdResumo, textAlign: 'left', fontWeight: 800, color: '#fff', borderBottom: 'none' }}>Total</td>
                                        <td style={{ ...tdResumo, fontWeight: 800, color: '#fff', borderBottom: 'none' }}>{propDesenhada.total}</td>
                                        <td style={{ ...tdResumo, fontWeight: 800, color: '#fff', borderBottom: 'none' }}>{propReal.total}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ ...tdResumo, textAlign: 'left', fontWeight: 800 }}>% 12x36</td>
                                        <td style={celProp(cD)} title={cD.rotulo}>{fmtPct(propDesenhada.pct)}</td>
                                        <td style={celProp(cR)} title={cR.rotulo}>{fmtPct(propReal.pct)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 6, lineHeight: 1.5, maxWidth: 520 }}>
                            🟢 65–75 · 🟡 61–65 e 75–79 · 🔴 &lt;61 e &gt;79.
                            {' '}<b>Desenhada</b>: o efetivo do desenho (12x36 em dobro).
                            {' '}<b>Real</b>: o crew ativo da loja (cadastro), só nos cargos que esta escala desenha —
                            cargo não desenhado fica fora dos dois lados.
                        </div>
                    </div>
                );
            })()}

            {/* ⚠️ O RESUMO DO EFETIVO fica EMBAIXO (Rodrigo, 15/09), junto dos avisos:
                é a conclusão da escala, e conclusão não vem antes do que ela conclui.
                No topo, empurrava a grade — que é o que se veio olhar — para baixo. */}
            <div style={{ marginTop: 16, border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', display: 'inline-block' }}>
                <table style={{ borderCollapse: 'collapse', fontSize: 12, background: '#fff' }}>
                    <thead>
                        <tr style={{ background: '#0f172a' }}>
                            <th style={{ ...thResumo, textAlign: 'left' }}>Cargo</th>
                            <th style={thResumo}>Escala</th>
                            <th style={thResumo} title="Pessoas trabalhando num dia da semana cheia">Posições</th>
                            <th style={thResumo} title="No 12x36 a mesma posição exige turma A e turma B">×</th>
                            <th style={thResumo}>Efetivo</th>
                            <th style={thResumo} title="Horas trabalhadas (fora o break) que a escala entrega num dia">Horas/dia</th>
                            <th style={thResumo} title="Estimativa: horas/dia × 30 — não desconta a folga do 5x2; no 12x36 já é o mês real">Horas/mês</th>
                        </tr>
                    </thead>
                    <tbody>
                        {resumo.linhas.length === 0 && (
                            <tr><td colSpan={7} style={{ padding: 14, color: '#94a3b8', textAlign: 'center' }}>Escala vazia.</td></tr>
                        )}
                        {resumo.linhas.map((r, i) => (
                            <tr key={i}>
                                <td style={{ ...tdResumo, textAlign: 'left', fontWeight: 700 }}>{r.cargo}</td>
                                <td style={tdResumo}>{r.escala}</td>
                                <td style={tdResumo}>{r.posicoes}</td>
                                <td style={{ ...tdResumo, color: r.fator === 2 ? COR_MODULO : '#94a3b8', fontWeight: 800 }}>{r.fator}</td>
                                <td style={{ ...tdResumo, fontWeight: 800 }}>{r.efetivo}</td>
                                <td style={tdResumo}>{r.horasDia}</td>
                                <td style={tdResumo}>{r.horasDia * 30}</td>
                            </tr>
                        ))}
                        {/* ⚠️ A cor do texto vai em CADA `td`, não só no `tr`: a linha de
                            cima pinta o `fator` explicitamente, e uma cor herdada do `tr`
                            perderia para ela — foi assim que o total saiu com a fonte
                            escura sobre o fundo preto. */}
                        <tr style={{ background: '#0f172a' }}>
                            <td style={{ ...tdResumo, textAlign: 'left', fontWeight: 800, color: '#fff', borderBottom: 'none' }} colSpan={2}>TOTAL</td>
                            <td style={{ ...tdResumo, fontWeight: 800, color: '#fff', borderBottom: 'none' }}>{resumo.totalPosicoes}</td>
                            <td style={{ ...tdResumo, color: '#fff', borderBottom: 'none' }}></td>
                            <td style={{ ...tdResumo, fontWeight: 800, color: '#fff', borderBottom: 'none' }}>{resumo.totalEfetivo}</td>
                            <td style={{ ...tdResumo, fontWeight: 800, color: '#fff', borderBottom: 'none' }}>{resumo.totalHorasDia}</td>
                            <td style={{ ...tdResumo, fontWeight: 800, color: '#fff', borderBottom: 'none' }}>{resumo.totalHorasDia * 30}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 6, maxWidth: 560, lineHeight: 1.5 }}>
                <b>Horas/dia</b>: horas trabalhadas (fora o break) que a escala desenhada entrega num dia.
                {' '}<b>Horas/mês</b>: × 30 — estimativa de capacidade; não desconta a folga do 5x2 (no 12x36 o ×30 já é o mês real).
            </div>

            {/* ⚠️ Os avisos ficam no RODAPÉ (Rodrigo, 15/09): eles comentam a
                escala, então só fazem sentido depois de vê-la. No topo, empurravam
                a grade para baixo e atrasavam justamente o que se veio olhar. */}
            {dados.avisos?.length > 0 && (
                <div style={{ ...aviso('#fef3c7', '#92400e'), marginTop: 14, marginBottom: 0 }}>
                    <b style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <AlertTriangle size={14} /> {dados.avisos.length} aviso(s)
                    </b>
                    <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
                        {dados.avisos.map((a, i) => (
                            <li key={i} style={{ marginBottom: 3 }}><b>{a.cargo} · {a.turno}</b> — {a.texto}</li>
                        ))}
                    </ul>
                    <div style={{ marginTop: 6, fontSize: 12, opacity: 0.85 }}>
                        Avisos nunca impedem de salvar. Recalculados a cada salvamento.
                    </div>
                </div>
            )}

        </div>
    );
};

const thRotulo = {
    width: 270, minWidth: 270, textAlign: 'left', padding: '0 10px',
    background: '#0f172a', fontSize: 11, color: '#fff', fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: 0.3, border: '1px solid #0f172a',
};
// ⚠️ Sem `borderRadius`: retângulo comum, a pedido do Rodrigo (14/09).
const thHora = {
    width: CEL_L, minWidth: CEL_L, padding: 0, textAlign: 'center',
    fontSize: 12, fontWeight: 700, border: '1px solid #0f172a', color: '#fff',
};
// Preto com branco, igual ao cabeçalho das horas — o cinza claro sobre o fundo
// da página sumia (Rodrigo, 14/09).
const thNum = {
    width: 62, minWidth: 62, padding: 0, textAlign: 'center',
    fontSize: 10, color: '#fff', background: '#0f172a', fontWeight: 700,
    textTransform: 'uppercase', border: '1px solid #0f172a',
};
const tdRotulo = {
    width: 270, padding: '0 10px', textAlign: 'left', fontSize: 12,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', border: 'none',
};
const tdHora = { width: CEL_L, height: CEL_A, padding: 0, textAlign: 'center', overflow: 'hidden' };
const tdNumero = { width: 62, padding: 0, textAlign: 'center', border: 'none' };
const thResumo = {
    padding: '7px 12px', textAlign: 'center', fontSize: 10, color: '#fff',
    fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3, whiteSpace: 'nowrap', border: 'none',
};
const tdResumo = { padding: '6px 12px', textAlign: 'center', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' };

export default RHDimensionamentoEscalaEditor;
